import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles, withStyles, useTheme } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Grid,
  Typography,
  TextareaAutosize,
  TextField,
  IconButton,
  useMediaQuery,
  Button,
  Badge,
  CircularProgress,
} from '@material-ui/core';
import { orange } from '@material-ui/core/colors';
import { Tune, Delete, Warning } from '@material-ui/icons';
import { ToggleButtonGroup, ToggleButton } from '@material-ui/lab';
import styled from 'styled-components';
import CustomDropdown from '../../../components/Common/Dropdown';
import { MARKETPLACE_ITEMS, MARKETPLACES_TABS, UPBEAT_ORG } from '../../../helpers/constants';
import { getUserOrg } from '../../../helpers/user';
import GroupSettingsModal from './modals/GroupSettingsModal';
import GroupDeleteModal from './modals/GroupDeleteModal';
import { NormalStyledDarkToggleButton, NormalStyledToggleButton } from '../../../components/Common/ToggleButton';
import DynamicSpaceoutSwitch from './DynamicSpaceoutSwitch';

const ExpandTextArea = styled.div``;

const useStyles = makeStyles({
  newNotificationFadeOut: {
    color: orange[500],
    marginTop: '-10px',
    marginLeft: '-2px',
    opacity: 0,
    transition: 'opacity 1s',
    fontSize: '16px',
  },
  newNotificationFadeIn: {
    color: orange[500],
    marginTop: '-10px',
    marginLeft: '-2px',
    fontSize: '16px',
    opacity: 1,
    transition: 'opacity 1s',
  },
  removeUnderline: {
    '& .MuiInput-underline.Mui-disabled:before': {
      borderBottomStyle: 'solid',
      borderBottomColor: 'lightgray',
    },
  },
  detailsMoveEnd: {
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  italicLabel: {
    fontStyle: 'italic',
  },
  InputContainers: {
    display: 'flex',
    gap: '20px',
  },
  buttonProgress: {
    // color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  savedLabel: {
    fontSize: '14px',
    color: '#4CA353',
  },
  customBadge: {
    backgroundColor: orange[500],
    height: 12,
    width: 12,
    borderRadius: 10,
  },
});

const PlusToggleButton = withStyles({
  root: {
    backgroundColor: '#F3F3F3',
    color: '#DBD8DC',
    minWidth: '21px',
    width: '21px',
    height: '21px',
    fontSize: 18,
    '&.Mui-selected': {
      color: '#166544',
      backgroundColor: '#DCFCE7',
      '&:hover': {
        backgroundColor: '#DCFCE7',
      },
      '&.Mui-disabled': {
        color: '#16654480',
        backgroundColor: '#DCFCE780',
        fontStyle: 'italic',
      },
    },
    '&.Mui-disabled': {
      color: '#DBD8DC',
      backgroundColor: '#EEEEEEAB',
      fontStyle: 'italic',
    },
  },
})(ToggleButton);

const PlusDarkToggleButton = withStyles({
  root: {
    backgroundColor: '#00000040',
    color: '#FFFFFF',
    minWidth: '21px',
    width: '21px',
    height: '21px',
    fontSize: 18,
    '&.Mui-selected': {
      color: '#166544',
      backgroundColor: '#DCFCE7',
      '&:hover': {
        backgroundColor: '#DCFCE7',
      },
      '&.Mui-disabled': {
        color: '#166544',
        backgroundColor: '#DCFCE799',
        fontStyle: 'italic',
      },
    },
    '&.Mui-disabled': {
      color: '#7D7D7D',
      backgroundColor: '#00000040',
      fontStyle: 'italic',
    },
  },
})(ToggleButton);

const MinusToggleButton = withStyles({
  root: {
    backgroundColor: '#F3F3F3',
    color: '#DBD8DC',
    minWidth: '21px',
    width: '21px',
    height: '21px',
    fontSize: 18,
    '&.Mui-selected': {
      color: '#991B1B',
      backgroundColor: '#FEE2E2',
      '&:hover': {
        backgroundColor: '#FEE2E2',
      },
      '&.Mui-disabled': {
        color: '#991B1B80',
        backgroundColor: '#FEE2E280',
        fontStyle: 'italic',
      },
    },
    '&.Mui-disabled': {
      color: '#DBD8DC',
      backgroundColor: '#EEEEEEAB',
      fontStyle: 'italic',
    },
  },
})(ToggleButton);

const initialState = {
  isPushEventModalOpen: false,
  isPriceAllLoading: false,
  isGroupSaveLoading: false,
};

const MinusDarkToggleButton = withStyles({
  root: {
    backgroundColor: '#00000040',
    color: '#FFFFFF',
    minWidth: '21px',
    width: '21px',
    height: '21px',
    fontSize: 18,
    '&.Mui-selected': {
      color: '#991B1B',
      backgroundColor: '#FEE2E2',
      '&:hover': {
        backgroundColor: '#FEE2E2',
      },
      '&.Mui-disabled': {
        color: '#991B1B',
        backgroundColor: '#FEE2E299',
        fontStyle: 'italic',
      },
    },
    '&.Mui-disabled': {
      color: '#DBD8DC',
      backgroundColor: '#EEEEEEAB',
      fontStyle: 'italic',
    },
  },
})(ToggleButton);

const GroupCriteria = props => {
  const {
    currentComps,
    errorFlags,
    focusedPricingGroup,
    groupedListings,
    handleCeilingError,
    handleGroupAttrEdit,
    handleMultipleAttrEdit,
    handleGroupCriteriaChanges,
    handleGroupMarketplaceSelection,
    handleMarketplaceChange,
    hasCompsSelected,
    handleOV,
    isCompAllEvent,
    isCompAllGroup,
    isSaving,
    marketplaces,
    pricingActions,
    saveZoneFilters,
    selectedIndex,
    selectedPricingGroup,
    setSelectedIndex,
    setSelectedPricingGroup,
    tags,
    typeListing,
    isLoading,
    isPricingLoading,
    currPricingGroups,
    saveAllSubmit,
    isPriceAllLoading,
    isGroupSaveLoading,
    groupCriteria,
    incrementSymbol,
    incrementSymbolDefault,
    setSymbolHandler,
    setSymbol,
    setMarketplaceTab,
    marketplaceTab,
    groupFocused,
    isPushEnable,
    badGroups,
    groupsBasicInfo,
    isDynamicFxEvent,
  } = props;

  function valueToNumber(value) {
    // Remove the dolar sign and convert the value on a float number
    if (typeof value === 'string') {
      const number = parseFloat(value.replace('$', ''));
      return number;
    }
    return value;
  }

  const classes = useStyles();
  const theme = useTheme();
  const [showTextArea, setShowTextArea] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const pageMD = useMediaQuery(theme.breakpoints.only('md'));
  const [deleteGroupModalOpen, setDeleteGroupModalOpen] = useState(false);
  const [isManualPriceDisable, setIsManualPriceDisable] = useState(true);

  const pricingStrategies = tags.map(tag => ({
    id: tag.strategy_id,
    label: tag.strategy_name,
  }));

  function getWindowDimensions() {
    const { innerWidth: width } = window;
    return width;
  }

  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  const mdResWith = 1650;
  const smResWith = 1350;

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setSelectedPricingGroup(groupFocused);
  }, [groupFocused]);

  useEffect(() => {
    const marketplace = selectedPricingGroup?.marketplace;
    const marketTab = MARKETPLACES_TABS[Array.isArray(marketplace) ? marketplace[0] : marketplace || ''];
    setMarketplaceTab(marketTab);
    let incrementValue = Number(selectedPricingGroup?.increment);
    if (Number.isNaN(incrementValue)) {
      incrementValue = 0;
    }
    if (selectedPricingGroup?.increment === undefined || incrementValue === 0) {
      setSymbol('-');
    } else if (incrementValue >= 0) {
      setSymbol('+');
    } else {
      setSymbol(incrementSymbolDefault);
    }
  }, [selectedPricingGroup, selectedPricingGroup?.increment]);

  // eslint-disable-next-line no-unused-vars
  const handleValue = (field, other = null, callback = null) => (e, newValue) => {
    e.persist();

    if (field === 'splits') {
      const value = other;
      handleGroupAttrEdit(field, value, null)(e);
    } else {
      const { value } = e.target;
      handleGroupAttrEdit(field, value, other)(e);
    }
    if (callback) {
      const { value } = e.target;
      callback(value);
    }
    if (field === 'marketplace') {
      const newMarket = newValue.props ? newValue.props.children : '';
      setMarketplaceTab(MARKETPLACES_TABS[newMarket.toLowerCase()]);
    }
  };

  const handleOnBlur = field => e => {
    const { value } = e.target;
    if (value) {
      handleGroupAttrEdit(field, value)(e);
    }
  };

  const isDisabled = () => {
    let errors = [];
    if (hasCompsSelected) {
      errors = errorFlags.filter(e => e !== 'manual_price' && e.length > 0);
    } else {
      errors = errorFlags.filter(e => e.length > 0);
    }

    return (
      !selectedPricingGroup ||
      errors.length > 0 ||
      selectedPricingGroup.increment === '' ||
      selectedPricingGroup.spaceout === '' ||
      selectedPricingGroup.auto_broadcast === ''
    );
  };

  useEffect(() => {
    setIsManualPriceDisable(!selectedPricingGroup || currentComps.length > 0 || isCompAllEvent || isCompAllGroup);
  }, [selectedPricingGroup, currentComps, isCompAllEvent, isCompAllGroup]);

  const handleSettingsModalOpen = event => {
    event.stopPropagation();
    setSettingsModalOpen(true);
  };

  const handleDeleteGroupModalOpen = event => {
    event.stopPropagation();
    setDeleteGroupModalOpen(true);
  };

  const [state, setState] = useState(initialState);

  const isPriceAllDisabled =
    isLoading || isPricingLoading || (currPricingGroups && currPricingGroups.length < 1) || isPriceAllLoading;

  const getSpaceoutValue = () => {
    if (!selectedPricingGroup) return '';
    if (selectedPricingGroup.dynamic_spaceout_enabled) return 0;
    return selectedPricingGroup.spaceout || '';
  };

  const editButtons = (
    <Grid item xs={12}>
      <Grid container direction="row" className={classes.detailsMoveEnd}>
        <Grid item xs={3} />
        <Grid item xs={3}>
          <IconButton
            title="Advanced Settings"
            disabled={isDisabled() || settingsModalOpen || (groupsBasicInfo && focusedPricingGroup !== null)}
            aria-label="Advanced Settings"
            onClick={handleSettingsModalOpen}
            color="primary"
          >
            <Tune fontSize="large" />
          </IconButton>
        </Grid>
        <Grid item xs={3}>
          <IconButton
            title="delete"
            aria-label="Delete"
            onClick={handleDeleteGroupModalOpen}
            color="primary"
            disabled={!selectedPricingGroup}
            data-testid="delete-group-button"
          >
            <Delete fontSize="large" />
          </IconButton>
        </Grid>
      </Grid>
    </Grid>
  );

  const groupSettingsModal = (
    <>
      {selectedPricingGroup && settingsModalOpen && (
        <GroupSettingsModal
          settingsModalOpen={settingsModalOpen}
          setSettingsModalOpen={setSettingsModalOpen}
          pricingGroup={selectedPricingGroup}
          index={selectedIndex}
          groupedListings={groupedListings}
          marketplaces={marketplaces}
          handleMarketplaceChange={handleMarketplaceChange}
          handleOV={handleOV}
          saveZoneFilters={saveZoneFilters}
          handleGroupCriteriaChanges={handleGroupCriteriaChanges}
          currentComps={currentComps}
          handleGroupAttrEdit={handleGroupAttrEdit}
          handleMultipleAttrEdit={handleMultipleAttrEdit}
        />
      )}
    </>
  );

  const deleteGroupModal = (
    <GroupDeleteModal
      deleteGroupModalOpen={deleteGroupModalOpen}
      setDeleteGroupModalOpen={setDeleteGroupModalOpen}
      pricingActions={pricingActions}
      pricingGroup={selectedPricingGroup}
      typeListing={typeListing}
      setSelectedPricingGroup={setSelectedPricingGroup}
      setSelectedIndex={setSelectedIndex}
    />
  );

  const nameItem = (
    <Grid item xs={12}>
      <TextField
        id="group-name-text-field"
        shrink={selectedPricingGroup && selectedPricingGroup.name}
        disabled={!selectedPricingGroup || isSaving}
        label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Group Name</Typography>}
        value={(selectedPricingGroup && selectedPricingGroup.name) || ''}
        onChange={handleValue('name')}
        type="text"
        autoFocus={selectedPricingGroup ? focusedPricingGroup === selectedPricingGroup.id : null}
        fullWidth
        className={classes.removeUnderline}
        inputProps={{ 'data-testid': 'group-name-text-field' }}
      />
    </Grid>
  );

  const marketplaceItem = (
    <Grid item xs={12}>
      <CustomDropdown
        label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Marketplace</Typography>}
        id="group-marketplace-select"
        name="group-marketplace"
        items={MARKETPLACE_ITEMS}
        disabled={!selectedPricingGroup || isSaving}
        width="100%"
        shrink={selectedPricingGroup && selectedPricingGroup.marketplace.length > 0}
        selectedValue={marketplaceTab ?? ''}
        defaultValue={marketplaceTab ?? ''}
        onChange={handleValue('marketplace', null, handleGroupMarketplaceSelection)}
        inputProps={{ 'data-testid': 'marketplace-drop-down' }}
      />
    </Grid>
  );

  const floorItem = (
    <Grid item xs={12}>
      <TextField
        id="group-floor-text-field"
        shrink={selectedPricingGroup && selectedPricingGroup.floor}
        disabled={!selectedPricingGroup || isSaving}
        label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Floor</Typography>}
        value={(selectedPricingGroup && valueToNumber(selectedPricingGroup.floor)) || ''}
        onChange={handleValue('floor')}
        error={errorFlags.includes('floor') && selectedPricingGroup}
        helperText={
          // eslint-disable-next-line no-nested-ternary
          selectedPricingGroup && errorFlags.includes('floor')
            ? selectedPricingGroup.floor
              ? 'Floor should be less than ceiling'
              : 'Floor can not be empty '
            : null
        }
        type="number"
        autoFocus={selectedPricingGroup ? focusedPricingGroup === selectedPricingGroup.id : null}
        fullWidth
        className={classes.removeUnderline}
        inputProps={{ 'data-testid': 'floor-text-field' }}
      />
    </Grid>
  );

  const ceilingItem = (
    <Grid item xs={12}>
      <TextField
        id="group-ceiling-text-field"
        shrink={selectedPricingGroup && selectedPricingGroup.ceiling}
        disabled={!selectedPricingGroup || isSaving}
        label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Ceiling</Typography>}
        value={(selectedPricingGroup && valueToNumber(selectedPricingGroup.ceiling)) || ''}
        onChange={handleValue('ceiling')}
        onBlur={handleOnBlur('ceiling', null)}
        error={handleCeilingError(errorFlags.includes('ceiling')) && selectedPricingGroup}
        type="number"
        fullWidth
        className={classes.removeUnderline}
        inputProps={{ 'data-testid': 'ceiling-text-field' }}
      />
    </Grid>
  );

  const manualPriceItem = (
    <Grid item xs={12}>
      <TextField
        id="group-manual-price-text-field"
        disabled={isManualPriceDisable || isSaving}
        error={errorFlags.includes('manual_price') && selectedPricingGroup && !isManualPriceDisable}
        label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Manual Price</Typography>}
        value={(!isManualPriceDisable && selectedPricingGroup && selectedPricingGroup.manual_price) || ''}
        onChange={handleValue('manual_price')}
        onBlur={handleOnBlur('manual_price', null)}
        type="number"
        fullWidth
        className={classes.removeUnderline}
        helperText={
          // eslint-disable-next-line no-nested-ternary
          selectedPricingGroup &&
          !isManualPriceDisable &&
          errorFlags.includes('manual_price') &&
          selectedPricingGroup.floor &&
          selectedPricingGroup.ceiling
            ? 'Manual price should be within floor and ceiling'
            : selectedPricingGroup &&
              !isManualPriceDisable &&
              errorFlags.includes('manual_price') &&
              selectedPricingGroup.floor &&
              selectedPricingGroup.ceiling === ''
            ? 'Manual price must be higher than Floor'
            : null
        }
      />
    </Grid>
  );

  const pricingStrategyItem = (
    <Grid
      container
      direction="column"
      alignItems="flex-start"
      style={{
        paddingRight: pageMD ? '6px' : '0px',
        height:
          pageMD && showTextArea && selectedPricingGroup && selectedPricingGroup.pricing_strategy_id > 0 && '120px',
      }}
    >
      <CustomDropdown
        label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Pricing Strategy</Typography>}
        shrink={selectedPricingGroup && selectedPricingGroup.pricing_strategy_id !== null}
        disabled={!selectedPricingGroup || isSaving}
        id="pricing-strategy-select"
        name="pricing-strategy"
        items={pricingStrategies}
        width="100%"
        selectedValue={
          selectedPricingGroup && selectedPricingGroup.pricing_strategy_id !== null
            ? selectedPricingGroup.pricing_strategy_id
            : ''
        }
        allowEmpty
        onChange={e => handleGroupAttrEdit('pricing_strategy_id', null)(e)}
      />
      {selectedPricingGroup && selectedPricingGroup.pricing_strategy_id > 0 && (
        <ExpandTextArea
          onClick={() => {
            setShowTextArea(!showTextArea);
          }}
        >
          <Typography>
            Comments
            {showTextArea ? ' ^' : ' v'}
          </Typography>
        </ExpandTextArea>
      )}
      {showTextArea && selectedPricingGroup && selectedPricingGroup.pricing_strategy_id > 0 && (
        <TextareaAutosize
          value={selectedPricingGroup ? selectedPricingGroup.pricing_strategy_comments : ''}
          onChange={handleValue('pricing_strategy_comments')}
          aria-label="minimum height"
          minRows={2}
          maxRows={3}
          style={{ maxWidth: 115, minWidth: 80, marginRight: 'auto' }}
        />
      )}
    </Grid>
  );

  const incrementItem = (
    <Grid container direction="row" xs={12} spacing={1} justifyContent={windowDimensions <= mdResWith ? 'center' : ''}>
      <Grid item style={{ width: '51px' }}>
        <ToggleButtonGroup
          value={incrementSymbol}
          style={{ marginTop: '25px' }}
          exclusive
          onChange={(e, value) => {
            e.persist();
            setSymbolHandler(e, value);
            handleGroupAttrEdit('increment_symbol', null)(e, value);
          }}
          size="small"
          disabled={!selectedPricingGroup || isSaving}
        >
          {theme.palette.type === 'dark' ? (
            <PlusDarkToggleButton value="+" disabled={!selectedPricingGroup}>
              +
            </PlusDarkToggleButton>
          ) : (
            <PlusToggleButton value="+" disabled={!selectedPricingGroup}>
              +
            </PlusToggleButton>
          )}
          {theme.palette.type === 'dark' ? (
            <MinusDarkToggleButton value="-" disabled={!selectedPricingGroup}>
              -
            </MinusDarkToggleButton>
          ) : (
            <MinusToggleButton value="-" disabled={!selectedPricingGroup}>
              -
            </MinusToggleButton>
          )}
        </ToggleButtonGroup>
      </Grid>
      <Grid item style={{ width: 'calc(100% - 102px)' }}>
        <TextField
          id="group-increment-text-field"
          shrink={selectedPricingGroup && selectedPricingGroup.increment}
          disabled={!selectedPricingGroup || isSaving}
          label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Increment</Typography>}
          value={
            selectedPricingGroup && selectedPricingGroup.increment !== ''
              ? Math.abs(selectedPricingGroup.increment)
              : ''
          }
          error={errorFlags.includes('increment') && selectedPricingGroup}
          helperText={selectedPricingGroup && errorFlags.includes('increment') ? 'Increment can not be empty' : ''}
          onChange={handleValue('increment', incrementSymbol)}
          type="number"
          fullWidth
          InputProps={{
            step: 0.1,
            style: { fontSize: 14, maxHeight: 22, marginTop: 26 },
            inputProps: { 'data-testid': 'increment-text-field' },
          }}
          className={classes.removeUnderline}
        />
      </Grid>
      <Grid item style={{ width: '51px' }}>
        <ToggleButtonGroup
          value={(selectedPricingGroup && selectedPricingGroup.increment_type) || '%'}
          style={{ marginTop: '25px' }}
          exclusive
          onChange={handleGroupAttrEdit('increment_type', null)}
          size="small"
          disabled={!selectedPricingGroup || isSaving}
        >
          {theme.palette.type === 'dark' ? (
            <NormalStyledDarkToggleButton value="$" disabled={!selectedPricingGroup}>
              $
            </NormalStyledDarkToggleButton>
          ) : (
            <NormalStyledToggleButton value="$" disabled={!selectedPricingGroup}>
              $
            </NormalStyledToggleButton>
          )}
          {theme.palette.type === 'dark' ? (
            <NormalStyledDarkToggleButton value="%" disabled={!selectedPricingGroup}>
              %
            </NormalStyledDarkToggleButton>
          ) : (
            <NormalStyledToggleButton value="%" disabled={!selectedPricingGroup}>
              %
            </NormalStyledToggleButton>
          )}
        </ToggleButtonGroup>
      </Grid>
    </Grid>
  );

  const spaceOutItem = (
    <Grid container direction="row" xs={12} justifyContent={windowDimensions <= mdResWith ? 'center' : ''} spacing={1}>
      <Grid item style={{ width: 'calc(100% - 60px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <DynamicSpaceoutSwitch
            key={selectedPricingGroup?.id || 'no-group'}
            selectedPricingGroup={selectedPricingGroup}
            isDynamicFxEvent={isDynamicFxEvent}
            handleGroupAttrEdit={handleGroupAttrEdit}
          />
          <TextField
            id="group-spaceout-text-field"
            shrink={selectedPricingGroup && selectedPricingGroup.spaceout}
            label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Spaceout</Typography>}
            value={getSpaceoutValue()}
            onChange={handleValue('spaceout')}
            error={errorFlags.includes('spaceout') && selectedPricingGroup}
            helperText={selectedPricingGroup && errorFlags.includes('spaceout') ? 'Spaceout can not be empty' : ''}
            disabled={!selectedPricingGroup || isSaving || selectedPricingGroup?.dynamic_spaceout_enabled}
            type="number"
            fullWidth
            InputProps={{
              step: 0.1,
              style: { fontSize: 14, maxHeight: 24, marginTop: 24 },
              inputProps: {
                'data-testid': 'spaceout-text-field',
              },
            }}
            className={classes.removeUnderline}
          />
        </div>
      </Grid>
      <Grid item alignContent="flex-start" style={{ width: '51px' }}>
        <ToggleButtonGroup
          value={(selectedPricingGroup && selectedPricingGroup.spaceout_type) || '%'}
          style={{ marginTop: '25px' }}
          exclusive
          onChange={handleGroupAttrEdit('spaceout_type', null)}
          size="small"
          disabled={!selectedPricingGroup || isSaving || selectedPricingGroup?.dynamic_spaceout_enabled}
        >
          {theme.palette.type === 'dark' ? (
            <NormalStyledDarkToggleButton
              value="$"
              disabled={!selectedPricingGroup || isSaving || selectedPricingGroup?.dynamic_spaceout_enabled}
            >
              $
            </NormalStyledDarkToggleButton>
          ) : (
            <NormalStyledToggleButton
              value="$"
              disabled={!selectedPricingGroup || isSaving || selectedPricingGroup?.dynamic_spaceout_enabled}
            >
              $
            </NormalStyledToggleButton>
          )}
          {theme.palette.type === 'dark' ? (
            <NormalStyledDarkToggleButton
              value="%"
              disabled={!selectedPricingGroup || isSaving || selectedPricingGroup?.dynamic_spaceout_enabled}
            >
              %
            </NormalStyledDarkToggleButton>
          ) : (
            <NormalStyledToggleButton
              value="%"
              disabled={!selectedPricingGroup || isSaving || selectedPricingGroup?.dynamic_spaceout_enabled}
            >
              %
            </NormalStyledToggleButton>
          )}
        </ToggleButtonGroup>
      </Grid>
    </Grid>
  );

  const autoBroadcastItem = (
    <Grid container xs={12} direction="row">
      <TextField
        id="group-auto-broadcast-text-field"
        shrink={selectedPricingGroup && selectedPricingGroup.auto_broadcast}
        label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Auto Broadcast</Typography>}
        disabled={!selectedPricingGroup || isSaving}
        value={(() => {
          if (selectedPricingGroup) {
            if (selectedPricingGroup.auto_broadcast && selectedPricingGroup.auto_broadcast >= 0) {
              return selectedPricingGroup.auto_broadcast;
            }
            return 0;
          }
          return '';
        })()}
        onChange={handleValue('auto_broadcast')}
        onBlur={handleGroupAttrEdit('auto_broadcast', null)}
        type="Number"
        fullWidth
        className={classes.removeUnderline}
      />
    </Grid>
  );

  const isChanged = selectedPricingGroup && groupCriteria[selectedPricingGroup.id];

  const minCostInGroup = useMemo(() => {
    if (!selectedPricingGroup || !selectedPricingGroup.ranks || !groupedListings) return null;
    const listingMap = new Map(groupedListings.map(l => [l.skybox_id, l]));
    const min = selectedPricingGroup.ranks.reduce((acc, r) => {
      const listing = listingMap.get(r.skybox_listing_id);
      if (!listing) return acc;
      const cost = listing.cost?.per_unit || 0;
      return cost < acc ? cost : acc;
    }, Infinity);
    return min === Infinity ? null : min;
  }, [selectedPricingGroup, groupedListings]);

  const isUpbeat = getUserOrg() === UPBEAT_ORG;

  const floorLessThanCost =
    isUpbeat && selectedPricingGroup && minCostInGroup !== null && Number(selectedPricingGroup.floor) < minCostInGroup;

  const noBroadcast = isUpbeat && selectedPricingGroup && !Number(selectedPricingGroup.auto_broadcast);

  const pricingWarnings =
    floorLessThanCost || noBroadcast ? (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {floorLessThanCost && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Warning style={{ fontSize: '14px', marginRight: '4px', color: '#d32f2f' }} />
            <Typography variant="caption" style={{ color: '#d32f2f', lineHeight: 1.3 }}>
              Floor less than cost
            </Typography>
          </div>
        )}
        {noBroadcast && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Warning style={{ fontSize: '14px', marginRight: '4px', color: '#d32f2f' }} />
            <Typography variant="caption" style={{ color: '#d32f2f', lineHeight: 1.3 }}>
              Nothing set to broadcast
            </Typography>
          </div>
        )}
      </div>
    ) : null;

  const saveButton = (
    <Badge variant="dot" size="large" classes={{ badge: classes.customBadge }} invisible={!isChanged}>
      <Button
        data-testid="group-criteria-save-button"
        color="primary"
        variant="contained"
        onClick={() => {
          saveAllSubmit(true);
          setState({ ...state, isGroupSaveLoading: true });
        }}
        disabled={isGroupSaveLoading || isPriceAllDisabled || badGroups.length > 0 || isDisabled()}
      >
        <Typography style={{ fontStyle: 'italic' }}>Save</Typography>
        {isGroupSaveLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
      </Button>
    </Badge>
  );

  const pushToButton = (
    <Button
      data-testid="group-criteria-push-to-sb-button"
      color="primary"
      variant="contained"
      onClick={() => {
        saveAllSubmit();
        setState({ ...state, isPriceAllLoading: true });
      }}
      disabled={isGroupSaveLoading || isPriceAllDisabled || badGroups.length > 0 || !isPushEnable}
    >
      <Typography style={{ fontStyle: 'italic' }}>Push to SB</Typography>
      {isPriceAllLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
    </Button>
  );

  return (
    <>
      <Grid data-testid="groups-group-criteria" container direction="column" style={{ padding: '15px 15px 15px 15px' }}>
        {windowDimensions <= mdResWith ? (
          <>
            <Grid item xs={12} className={classes.InputContainers}>
              <div style={{ flex: 3 }}>{nameItem}</div>
              <div style={{ flex: 3, marginLeft: 'auto' }}>{editButtons}</div>
            </Grid>

            <Grid item xs={12} className={classes.InputContainers} style={{ marginTop: '20px' }}>
              <div style={{ flex: 1 }}>{marketplaceItem}</div>
              <div style={{ flex: 0.5 }}>{floorItem}</div>
              <div style={{ flex: 0.5 }}>{ceilingItem}</div>
              <div style={{ flex: 1 }}>{manualPriceItem}</div>
              <div style={{ flex: 1 }}>{pricingStrategyItem}</div>
            </Grid>

            <Grid
              item
              flex
              xs={12}
              className={classes.InputContainers}
              style={{
                marginTop: '20px',
                maxHeight:
                  showTextArea && selectedPricingGroup && selectedPricingGroup.pricing_strategy_id > 0
                    ? '120px'
                    : '75px',
              }}
            >
              <div
                style={{
                  width: windowDimensions > smResWith ? '30%' : '30%',
                }}
              >
                {pricingStrategyItem}
              </div>
              <div
                style={{
                  width: windowDimensions > smResWith ? '30%' : '43%',
                  marginLeft: 'auto',
                }}
              >
                {incrementItem}
              </div>
              <div
                style={{
                  width: windowDimensions > smResWith ? '60%' : '57%',
                  borderLeft: 'auto',
                }}
              >
                {spaceOutItem}
              </div>
            </Grid>
            <Grid item xs={12} className={classes.InputContainers}>
              <div style={{ width: '25%' }}>{autoBroadcastItem}</div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '10px',
                  marginLeft: 'auto',
                  marginBottom: '4px',
                }}
              >
                {pricingWarnings && <div style={{ marginRight: '8px' }}>{pricingWarnings}</div>}
                <div style={{ textAlign: 'right' }}>{saveButton}</div>
                <div style={{ width: '10px' }} />
                <div style={{ textAlign: 'right' }}>{pushToButton}</div>
              </div>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} style={{ maxHeight: '60px' }}>
              <Grid container direction="row" className={classes.InputContainers}>
                <Grid item xs={6} style={{ flex: 1 }}>
                  <Grid item xs={12}>
                    {nameItem}
                  </Grid>
                </Grid>
                <Grid item xs={3} style={{ flex: 1 }} />
                <Grid item xs={3} style={{ flex: 1, marginLeft: 'auto' }}>
                  {editButtons}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container direction="row" className={classes.InputContainers}>
                <Grid item xs={3} style={{ flex: 1 }}>
                  {marketplaceItem}
                </Grid>
                <Grid item xs={5} style={{ flex: 1 }}>
                  <Grid item xs={12} className={classes.InputContainers}>
                    <div style={{ flex: 1 }}>{floorItem}</div>
                    <div style={{ flex: 1 }}>{ceilingItem}</div>
                  </Grid>
                </Grid>
                <Grid item xs={2} style={{ flex: 1 }}>
                  {manualPriceItem}
                </Grid>
                <Grid item xs={2} style={{ flex: 1, width: '80%' }}>
                  {pricingStrategyItem}
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container direction="row" display="flex" justifyContent="space-between">
                <Grid item style={{ width: '27%' }}>
                  {incrementItem}
                </Grid>
                <Grid item style={{ width: '35%' }}>
                  {spaceOutItem}
                </Grid>
                <Grid item style={{ width: '27%', marginTop: '3px' }}>
                  {autoBroadcastItem}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} style={{ marginTop: '10px' }}>
              <Grid container direction="row" display="flex" justifyContent="flex-end" alignItems="center">
                {pricingWarnings && (
                  <Grid item style={{ marginRight: '8px' }}>
                    {pricingWarnings}
                  </Grid>
                )}
                <Grid item style={{ display: 'flex' }}>
                  <Grid container style={{ display: 'flex', justifyContent: 'center', alignItems: 'end' }} spacing={1}>
                    <Grid item>{saveButton}</Grid>
                    <Grid item>{pushToButton}</Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
      {deleteGroupModal}
      {groupSettingsModal}
    </>
  );
};

GroupCriteria.defaultProps = {
  focusedPricingGroup: null,
  marketplaces: null,
  selectedPricingGroup: null,
};

GroupCriteria.propTypes = {
  currentComps: PropTypes.array.isRequired,
  errorFlags: PropTypes.array.isRequired,
  focusedPricingGroup: PropTypes.number,
  groupedListings: PropTypes.array.isRequired,
  handleCeilingError: PropTypes.func.isRequired,
  handleGroupAttrEdit: PropTypes.func.isRequired,
  handleGroupCriteriaChanges: PropTypes.func.isRequired,
  handleGroupMarketplaceSelection: PropTypes.func.isRequired,
  handleMarketplaceChange: PropTypes.func.isRequired,
  handleOV: PropTypes.func.isRequired,
  hasCompsSelected: PropTypes.bool.isRequired,
  isCompAllEvent: PropTypes.bool.isRequired,
  isCompAllGroup: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  marketplaces: PropTypes.object,
  pricingActions: PropTypes.object.isRequired,
  saveZoneFilters: PropTypes.func.isRequired,
  selectedIndex: PropTypes.number.isRequired,
  selectedPricingGroup: PropTypes.object,
  setSelectedIndex: PropTypes.func.isRequired,
  setSelectedPricingGroup: PropTypes.func.isRequired,
  tags: PropTypes.array.isRequired,
  typeListing: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isPricingLoading: PropTypes.bool.isRequired,
  currPricingGroups: PropTypes.array.isRequired,
  saveAllSubmit: PropTypes.func.isRequired,
  isPriceAllLoading: PropTypes.bool.isRequired,
  isGroupSaveLoading: PropTypes.bool.isRequired,
  groupCriteria: PropTypes.array.isRequired,
  incrementSymbol: PropTypes.string.isRequired,
  incrementSymbolDefault: PropTypes.string.isRequired,
  setSymbolHandler: PropTypes.func.isRequired,
  setSymbol: PropTypes.func.isRequired,
  setMarketplaceTab: PropTypes.func.isRequired,
  marketplaceTab: PropTypes.number.isRequired,
  groupFocused: PropTypes.object.isRequired,
  isPushEnable: PropTypes.bool.isRequired,
  badGroups: PropTypes.array.isRequired,
  groupsBasicInfo: PropTypes.bool.isRequired,
  isDynamicFxEvent: PropTypes.bool.isRequired,
  handleMultipleAttrEdit: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    groupFocused: state.refreshGroupsInventory.groupFocused,
    isPushEnable: state.refreshGroups.isPushEnable,
    isCompAllEvent: state.groupsStubhubSeatingState.isCompAllEvent,
    badGroups: state.groups.badGroups,
    groupsBasicInfo: state.refreshGroups.groupsBasicInfo,
  };
}

export default connect(mapStateToProps)(GroupCriteria);
